import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserPlus, Search, X, CheckCircle2, Phone, Mail,
} from "lucide-react";
import { searchDrivers, createDriver, searchCompanyDrivers } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Phase E driver picker — typeahead + "+ Add new driver".
 *
 * Props:
 *   value:           assigned_driver_id (string) or ""
 *   onChange(id):    setter
 *   clientId:        booking's client_id (for scope)
 *   bookerId:        approved_booker_id or null (re-ranks suggestions)
 *   prefillName:     "John Smith" (optional — from Doc-AI)
 *   prefillPhone:    "0411..."     (optional — from Doc-AI)
 *   disabled:        boolean
 */
export default function DriverPicker({
  value,
  onChange,
  onCaptureLicence = null,
  clientId,
  bookerId = null,
  prefillName = "",
  prefillPhone = "",
  currentLicence = null,
  disabled = false,
  required = false,
}) {
  const { hasRole } = useAuth();
  // Carrier-only users can only search their own company's drivers.
  const companyOnly = hasRole(["approved_booker"])
    && !hasRole(["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // driver row
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    licence_type:   currentLicence?.type   || "",
    licence_number: currentLicence?.number || "",
    licence_state:  currentLicence?.state  || "",
  });
  const debounceRef = useRef();

  // Pre-fill name/phone from Doc-AI when present — initialise draft from props once.
  const [draftInitialized, setDraftInitialized] = useState(false);
  useEffect(() => {
    if (draftInitialized) return;
    if (!prefillName && !prefillPhone) {
      setDraftInitialized(true);
      return;
    }
    const next = { ...draft };
    if (prefillName) {
      const [first, ...rest] = prefillName.trim().split(/\s+/);
      next.first_name = next.first_name || first || "";
      next.last_name  = next.last_name  || rest.join(" ");
    }
    if (prefillPhone && !next.phone) next.phone = prefillPhone;
    setDraft(next);
    setDraftInitialized(true);
  }, [prefillName, prefillPhone, draftInitialized, draft]);

  // Hydrate selected from value (edit-mode) — with cancellation guard.
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = companyOnly
        ? await searchCompanyDrivers({ ids: [value], limit: 1 })
        : await searchDrivers({ q: "", clientId, bookerId, limit: 1, ids: [value] });
      if (cancelled) return;
      const found = (data || []).find((d) => d.id === value);
      if (found) setSelected(found);
    })();
    return () => { cancelled = true; };
  }, [value, clientId, bookerId, companyOnly]);

  // Debounced search — cancellation-guarded.
  useEffect(() => {
    if (selected) return;
    if (q.length === 0) {
      setResults([]);
      return;
    }
    let cancelled = false;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true);
      const { data } = await searchDrivers({ q, clientId, bookerId, limit: 10, companyOnly });
      if (cancelled) return;
      setResults(data || []);
      setLoading(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [q, clientId, bookerId, selected, companyOnly]);

  const pick = (d) => {
    setSelected(d);
    setQ("");
    setResults([]);
    onChange(d.id);
  };

  const clear = () => {
    setSelected(null);
    onChange("");
  };

  const submitNew = async () => {
    if (!draft.first_name?.trim() || !draft.last_name?.trim() || !draft.email?.trim()) {
      toast.error("First name, last name, and email are required");
      return;
    }
    if (!clientId) {
      toast.error("No tenant context — cannot create driver");
      return;
    }
    setAdding(true);
    try {
      const r = await createDriver({
        firstName:     draft.first_name.trim(),
        lastName:      draft.last_name.trim(),
        email:         draft.email.trim().toLowerCase(),
        phone:         draft.phone?.trim() || null,
        licenceType:   draft.licence_type?.trim() || null,
        licenceNumber: draft.licence_number?.trim() || null,
        licenceState:  draft.licence_state?.trim() || null,
        clientId,
      });
      if (r.existing && r.matched) {
        toast(`Existing driver matched by ${r.matched}.`, { description: "Linked to this booking." });
      } else {
        toast.success("Driver created.");
      }
      // Hydrate the selected card with what we know
      setSelected({
        id: r.driver_user_id,
        first_name: draft.first_name,
        last_name:  draft.last_name,
        email:      draft.email,
        phone:      draft.phone,
        _scope:     "new",
      });
      onChange(r.driver_user_id);
      // Snapshot the licence onto the booking row so QA shows it without
      // having to fetch the driver profile separately.
      onCaptureLicence?.({
        type:   draft.licence_type,
        number: draft.licence_number,
        state:  draft.licence_state,
      });
      setAddOpen(false);
      setDraft({
        first_name: "", last_name: "", email: "", phone: "",
        licence_type: "", licence_number: "", licence_state: "",
      });
    } catch (e) {
      toast.error(e.message || "Driver create failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="driver-picker">
      <Label className="text-xs uppercase tracking-wider text-slate-500">
        Driver
        {required && <span className="text-rose-600 ml-0.5" title="Required — or tick Driver TBA" data-testid="required-mark-driver">*</span>}
      </Label>

      {selected && (
        <div className="flex items-center justify-between rounded-md border bg-emerald-50 px-3 py-2" data-testid="driver-selected">
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {[selected.first_name, selected.last_name].filter(Boolean).join(" ") || selected.email}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                {selected.email && (<><Mail className="h-3 w-3" />{selected.email}</>)}
                {selected.phone && (<><Phone className="h-3 w-3" />{selected.phone}</>)}
                {selected._scope === "booker" && <Badge variant="secondary" className="text-[10px]">used by this booker</Badge>}
                {selected._scope === "tenant" && <Badge variant="secondary" className="text-[10px]">used by this tenant</Badge>}
                {selected._scope === "new"    && <Badge className="bg-blue-100 text-blue-700 text-[10px]">new</Badge>}
              </div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={clear} disabled={disabled} data-testid="driver-clear">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {!selected && !addOpen && (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type driver name, email, or phone…"
              className="pl-9"
              disabled={disabled}
              data-testid="driver-search"
            />
          </div>

          {q.length > 0 && (
            <div className="rounded-md border bg-white shadow-sm max-h-60 overflow-y-auto" data-testid="driver-results">
              {loading && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
              {!loading && results.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500">No match. Try adding a new driver below.</div>
              )}
              {results.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => pick(d)}
                  data-testid={`driver-result-${d.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
                >
                  <div className="text-sm font-medium">
                    {[d.first_name, d.last_name].filter(Boolean).join(" ") || d.email}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    {d.email}{d.phone && ` · ${d.phone}`}
                    {d._scope === "booker" && <Badge variant="secondary" className="text-[10px]">used by this booker</Badge>}
                    {d._scope === "tenant" && <Badge variant="secondary" className="text-[10px]">used by this tenant</Badge>}
                  </div>
                </button>
              ))}
            </div>
          )}

          <Button type="button" variant="outline" size="sm" onClick={() => setAddOpen(true)} disabled={disabled} data-testid="driver-add-new">
            <UserPlus className="h-4 w-4 mr-1.5" /> Add new driver
          </Button>
        </div>
      )}

      {addOpen && !selected && (
        <div className="rounded-md border bg-slate-50 p-3 space-y-2" data-testid="driver-add-form">
          <div className="text-xs font-semibold text-slate-600 mb-1">New driver</div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="First name *" value={draft.first_name}
                   onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
                   data-testid="driver-first" />
            <Input placeholder="Last name *" value={draft.last_name}
                   onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
                   data-testid="driver-last" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="email" placeholder="Email *" value={draft.email}
                   onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                   data-testid="driver-add-email" />
            <Input placeholder="Phone (optional)" value={draft.phone}
                   onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                   data-testid="driver-add-phone" />
          </div>

          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 pt-1">
            Driver licence (optional)
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Select value={draft.licence_type || "_none_"}
                    onValueChange={(v) => setDraft({ ...draft, licence_type: v === "_none_" ? "" : v })}>
              <SelectTrigger data-testid="driver-add-licence-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">—</SelectItem>
                <SelectItem value="LR">LR</SelectItem>
                <SelectItem value="MR">MR</SelectItem>
                <SelectItem value="HR">HR</SelectItem>
                <SelectItem value="HC">HC</SelectItem>
                <SelectItem value="MC">MC</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Number" value={draft.licence_number}
                   onChange={(e) => setDraft({ ...draft, licence_number: e.target.value })}
                   data-testid="driver-add-licence" />
            <Select value={draft.licence_state || "_none_"}
                    onValueChange={(v) => setDraft({ ...draft, licence_state: v === "_none_" ? "" : v })}>
              <SelectTrigger data-testid="driver-add-licence-state"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">—</SelectItem>
                <SelectItem value="ACT">ACT</SelectItem>
                <SelectItem value="NSW">NSW</SelectItem>
                <SelectItem value="NT">NT</SelectItem>
                <SelectItem value="QLD">QLD</SelectItem>
                <SelectItem value="SA">SA</SelectItem>
                <SelectItem value="TAS">TAS</SelectItem>
                <SelectItem value="VIC">VIC</SelectItem>
                <SelectItem value="WA">WA</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button type="button" size="sm" onClick={submitNew} disabled={adding} data-testid="driver-add-submit">
              {adding ? "Adding…" : "Create driver"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            We dedup by email + phone. The driver gets their welcome email automatically when the booking is saved.
          </p>
        </div>
      )}
    </div>
  );
}
